import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { flatMap, map, size } from 'lodash';
import pluralize from 'pluralize';
import React from 'react';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { SlideInputValidation } from './slideInputValidations';

export interface SlideInputValidationSectionProps {
  validation: SlideInputValidation;
  removeSelectedModels: (slideIds: string[]) => void;
}

const SlideInputValidationSection: React.FC<React.PropsWithChildren<SlideInputValidationSectionProps>> = ({
  validation,
  removeSelectedModels,
}) => {
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();

  const slidesCountForAllStains = size(flatMap(validation.slidesByStain));
  const hasMultipleSlidesForAllStains = slidesCountForAllStains > 1;

  return (
    <Grid item mb={2}>
      <Alert severity={validation.severity} sx={{ mb: 1 }}>
        <Typography>{validation.title}</Typography>
        <Typography variant="body2">
          {validation.description} Review the {pluralize('slide', slidesCountForAllStains)} below or remove{' '}
          {hasMultipleSlidesForAllStains ? 'them' : 'it'} from the manifest.
        </Typography>
        {map(validation.slidesByStain, (slidesForValidationStain, stainType) => {
          const hasMultipleSlidesForStain = size(slidesForValidationStain) > 1;
          const stainLabel = isLoadingStainTypeOptions ? 'Loading...' : stainTypeIdToDisplayName(stainType);
          return (
            <Accordion
              key={`${validation.type}-${stainType}`}
              disableGutters
              elevation={0}
              square
              sx={{
                mt: 1,
                bgcolor: 'transparent',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}
              >
                <Grid container alignItems="center" spacing={1} wrap="nowrap">
                  {isLoadingStainTypeOptions && (
                    <Grid item>
                      <CircularProgress size={16} />
                    </Grid>
                  )}
                  <Grid item>
                    <Typography variant="subtitle2">
                      {stainLabel} ({size(slidesForValidationStain)})
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0 }}>
                <Grid container spacing={1} direction="column" sx={{ mb: 1 }}>
                  {map(slidesForValidationStain, (slide) => (
                    <Grid item key={slide.slideId}>
                      <Typography>{slide.slideId}</Typography>
                    </Grid>
                  ))}
                </Grid>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item>
                    <Typography>
                      Remove {hasMultipleSlidesForStain ? 'these' : 'this'}{' '}
                      {pluralize('slide', size(slidesForValidationStain))} from the manifest?
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Button onClick={() => removeSelectedModels(map(slidesForValidationStain, 'slideId'))}>
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Alert>
    </Grid>
  );
};

export default SlideInputValidationSection;
